import { useState, useEffect, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import { Plus, Trash2, X, Loader2, Sparkles, DiamondPlus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { AutoTranslate } from "@/lib/autoTranslate.tsx";
import { skillsRecommendations } from "@/hooks/skills-recommendations";
import { clearFormDraft, readFormDraft, writeFormDraft } from "@/lib/formDraftStorage";

interface Education {
  id: string;
  schoolName: string;
  degree: string;
  startMonth: string;
  startYear: string;
  endMonth: string;
  endYear: string;
  awards?: string;
  grades?: string;
  project?: string;
  datesDisabled: boolean;
}

interface Language {
  id: string;
  name: string;
  proficiency: string;
}

interface Certification {
  id: string;
  name: string;
  month: string;
  year: string;
}

interface Referee {
  id: string;
  name: string;
  position: string;
  company: string;
  email: string;
}

interface FormData {
  education: Education[];
  softSkills: string[];
  technicalSkills: string[];
  languages: Language[];
  certifications: Certification[];
  referees: Referee[];
}

interface EducationSkillsFormProps {
  onSubmit: (data: FormData) => void;
  suggestedSoftSkills?: string[];
  suggestedTechnicalSkills?: string[];
  jobTitle?: string
}

interface RecommendedSkill {
  name: string;
  skill_type: string | null;
}

const years = Array.from({ length: 50 }, (_, i) => (new Date().getFullYear() - i).toString());

interface EducationSkillsDraft {
  formData: FormData;
  softSkillInput: string;
  technicalSkillInput: string;
}

const createEmptyEducation = (): Education => ({
  id: crypto.randomUUID(),
  schoolName: "",
  degree: "",
  startMonth: "",
  startYear: "",
  endMonth: "",
  endYear: "",
  awards: "",
  grades: "",
  project: "",
  datesDisabled: false,
});

const createEmptyLanguage = (): Language => ({
  id: crypto.randomUUID(),
  name: "English",
  proficiency: "Native"
});

const createEmptyEducationSkillsFormData = (): FormData => ({
  education: [createEmptyEducation()],
  softSkills: [],
  technicalSkills: [],
  languages: [createEmptyLanguage()],
  certifications: [],
  referees: [],
});

const normalizeEducationSkillsDraft = (draft: Partial<EducationSkillsDraft>): EducationSkillsDraft => {
  const fallback = createEmptyEducationSkillsFormData();
  const formData = draft.formData || fallback;

  return {
    formData: {
      education: Array.isArray(formData.education) && formData.education.length > 0
        ? formData.education.map((education) => ({
          ...createEmptyEducation(),
          ...education,
          id: education.id || crypto.randomUUID()
        }))
        : fallback.education,
      softSkills: Array.isArray(formData.softSkills) ? formData.softSkills : [],
      technicalSkills: Array.isArray(formData.technicalSkills) ? formData.technicalSkills : [],
      languages: Array.isArray(formData.languages) ? formData.languages.map((language) => ({
        ...createEmptyLanguage(),
        ...language,
        id: language.id || crypto.randomUUID()
      })) : fallback.languages,
      certifications: Array.isArray(formData.certifications) ? formData.certifications.map((certification) => ({
        id: certification.id || crypto.randomUUID(),
        name: certification.name || "",
        month: certification.month || "",
        year: certification.year || ""
      })) : [],
      referees: Array.isArray(formData.referees) ? formData.referees.map((referee) => ({
        id: referee.id || crypto.randomUUID(),
        name: referee.name || "",
        position: referee.position || "",
        company: referee.company || "",
        email: referee.email || ""
      })) : [],
    },
    softSkillInput: draft.softSkillInput || "",
    technicalSkillInput: draft.technicalSkillInput || ""
  };
};

export function EducationSkillsForm({
  onSubmit,
  suggestedSoftSkills = [],
  suggestedTechnicalSkills = [],
  jobTitle = "",
}: EducationSkillsFormProps) {
  const { t } = useTranslation();

  const months = JSON.parse(t("forms.education.months"));
  const proficiencyLevels = JSON.parse(t("forms.education.proficiencyLevels"));

  const labelsTrans = t("forms.education.labels", { returnObjects: true }) as Record<string, string>;
  const placeholdersTrans = t("forms.education.placeholders", { returnObjects: true }) as Record<string, string>;
  const buttonsTrans = t("forms.education.buttons", { returnObjects: true }) as Record<string, string>;

  const [draftSeed] = useState<EducationSkillsDraft>(() => (
    normalizeEducationSkillsDraft(readFormDraft('educationSkills', {
      formData: createEmptyEducationSkillsFormData(),
      softSkillInput: "",
      technicalSkillInput: ""
    }))
  ));

  const [formData, setFormData] = useState<FormData>(draftSeed.formData);

  const [softSkillInput, setSoftSkillInput] = useState(draftSeed.softSkillInput);
  const [technicalSkillInput, setTechnicalSkillInput] = useState(draftSeed.technicalSkillInput);
  const [skillsRecommendationsList, setSkillsRecommendationsList] = useState<RecommendedSkill[]>([]);
  const [isRecommendationLoading, setIsRecommendationLoading] = useState(false);
  const [debouncedJobTitle, setDebouncedJobTitle] = useState("");
  const lastFetchedTitleRef = useRef("");

  useEffect(() => {
    writeFormDraft('educationSkills', {
      formData,
      softSkillInput,
      technicalSkillInput
    });
  }, [formData, softSkillInput, technicalSkillInput]);

  const normalizeSkillType = (type: string | null | undefined) => (type || "").trim().toLowerCase();

  const recommendedSoftSkills = skillsRecommendationsList.filter((skill) =>
    normalizeSkillType(skill.skill_type).includes("soft")
  );

  const recommendedTechnicalSkills = skillsRecommendationsList.filter((skill) => {
    const skillType = normalizeSkillType(skill.skill_type);
    return skillType.includes("tech") || skillType.includes("technical");
  });

  const addEducation = () => {
    setFormData({
      ...formData,
      education: [...formData.education, createEmptyEducation()],
    });
  };

  const removeEducation = (id: string) => {
    setFormData({
      ...formData,
      education: formData.education.filter((e) => e.id !== id),
    });
  };

  const updateEducation = (id: string, field: keyof Education, value: string | boolean) => {
    setFormData({
      ...formData,
      education: formData.education.map((e) => (e.id === id ? { ...e, [field]: value } : e)),
    });
  };

  const addSkill = (type: "soft" | "technical", skill: string) => {
    const trimmedSkill = skill.trim();
    if (!trimmedSkill) return;

    if (type === "soft") {
      const exists = formData.softSkills.some((item) => item.toLowerCase() === trimmedSkill.toLowerCase());
      if (exists) {
        setSoftSkillInput("");
        return;
      }
      setFormData({ ...formData, softSkills: [...formData.softSkills, trimmedSkill] });
      setSoftSkillInput("");
    } else {
      const exists = formData.technicalSkills.some((item) => item.toLowerCase() === trimmedSkill.toLowerCase());
      if (exists) {
        setTechnicalSkillInput("");
        return;
      }
      setFormData({ ...formData, technicalSkills: [...formData.technicalSkills, trimmedSkill] });
      setTechnicalSkillInput("");
    }
  };

  const removeSkill = (type: "soft" | "technical", skill: string) => {
    if (type === "soft") {
      setFormData({ ...formData, softSkills: formData.softSkills.filter((s) => s !== skill) });
    } else {
      setFormData({ ...formData, technicalSkills: formData.technicalSkills.filter((s) => s !== skill) });
    }
  };

  const hasSoftSkill = (name: string) =>
    formData.softSkills.some((skill) => skill.toLowerCase() === name.trim().toLowerCase());

  const hasTechnicalSkill = (name: string) =>
    formData.technicalSkills.some((skill) => skill.toLowerCase() === name.trim().toLowerCase());

  const fetchSkillsRecommendations = async (title: string) => {
    try {
      const recommendedSkills = await skillsRecommendations(title, []);
      setSkillsRecommendationsList(recommendedSkills);
    } catch (error) {
      console.error("Failed to fetch skills recommendations:", error);
      setSkillsRecommendationsList([]);
    } finally {
      setIsRecommendationLoading(false);
    }
  };

  useEffect(() => {
    const trimmedJobTitle = (jobTitle || "").trim();

    const timeout = setTimeout(() => {
      setDebouncedJobTitle(trimmedJobTitle);
    }, 700);

    return () => clearTimeout(timeout);
  }, [jobTitle]);

  useEffect(() => {
    const title = debouncedJobTitle.trim();
    if (!title || title.length < 3) {
      setSkillsRecommendationsList([]);
      return;
    }

    const fetchKey = title.toLowerCase();
    if (lastFetchedTitleRef.current === fetchKey) return;

    lastFetchedTitleRef.current = fetchKey;
    setIsRecommendationLoading(true);
    setSkillsRecommendationsList([]);
    fetchSkillsRecommendations(title);
  }, [debouncedJobTitle]);

  const addLanguage = () => {
    setFormData({
      ...formData,
      languages: [...formData.languages, { id: crypto.randomUUID(), name: "", proficiency: "Intermediate" }],
    });
  };

  const removeLanguage = (id: string) => {
    setFormData({
      ...formData,
      languages: formData.languages.filter((l) => l.id !== id),
    });
  };

  const updateLanguage = (id: string, field: "name" | "proficiency", value: string) => {
    setFormData({
      ...formData,
      languages: formData.languages.map((l) => (l.id === id ? { ...l, [field]: value } : l)),
    });
  };

  const addCertification = () => {
    setFormData({
      ...formData,
      certifications: [...formData.certifications, { id: crypto.randomUUID(), name: "", month: "", year: "" }],
    });
  };

  const removeCertification = (id: string) => {
    setFormData({
      ...formData,
      certifications: formData.certifications.filter((c) => c.id !== id),
    });
  };

  const updateCertification = (id: string, field: keyof Certification, value: string) => {
    setFormData({
      ...formData,
      certifications: formData.certifications.map((c) => (c.id === id ? { ...c, [field]: value } : c)),
    });
  };

  const addReferee = () => {
    setFormData({
      ...formData,
      referees: [...formData.referees, { id: crypto.randomUUID(), name: "", position: "", company: "", email: "" }],
    });
  };

  const removeReferee = (id: string) => {
    setFormData({
      ...formData,
      referees: formData.referees.filter((r) => r.id !== id),
    });
  };

  const updateReferee = (id: string, field: keyof Referee, value: string) => {
    setFormData({
      ...formData,
      referees: formData.referees.map((r) => (r.id === id ? { ...r, [field]: value } : r)),
    });
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    clearFormDraft('educationSkills');
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-8 p-6 bg-card rounded-lg border max-h-[70vh] overflow-y-auto">
      <h2 className="text-2xl font-bold sticky top-0 bg-card z-10 pb-2">{labelsTrans["skills"]}</h2>

      <div className="space-y-4">
        <h3 className="text-xl font-semibold">{labelsTrans["education"]}</h3>
        {formData.education.map((edu) => (
          <div key={edu.id} className="space-y-4 p-4 border rounded-lg relative">
            {formData.education.length > 1 && (
              <Button
                type="button"
                variant="destructive"
                size="icon"
                className="absolute top-2 right-2"
                aria-label="Remove education"
                onClick={() => removeEducation(edu.id)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}

            <div className="space-y-2">
              <Label htmlFor={`school-name-${edu.id}`}>{labelsTrans["schoolName"]}</Label>
              <Input
                id={`school-name-${edu.id}`}
                required
                value={edu.schoolName}
                onChange={(e) => updateEducation(edu.id, "schoolName", e.target.value)}
                placeholder="School name, City, Country e.g. Harvard University, Cambridge, USA"
                className="placeholder:text-xs"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor={`degree-${edu.id}`}>{labelsTrans["degree"]}</Label>
              <Input
                id={`degree-${edu.id}`}
                required
                value={edu.degree}
                onChange={(e) => updateEducation(edu.id, "degree", e.target.value)}
                placeholder="Bachelor of Science in Computer Science"
              />
            </div>

            <div className="flex items-center space-x-2 mb-4">
              <Label htmlFor={`include-dates-${edu.id}`}>{labelsTrans["includeDates"]}</Label>
              <input
                id={`include-dates-${edu.id}`}
                type="checkbox"
                checked={!edu.datesDisabled}
                onChange={(e) => updateEducation(edu.id, "datesDisabled", !e.target.checked)}
                className="w-4 h-4"
              />
            </div>

            {!edu.datesDisabled && (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor={`start-month-${edu.id}`}>{labelsTrans["startMonth"]}</Label>
                    <Select value={edu.startMonth} onValueChange={(value) => updateEducation(edu.id, "startMonth", value)}>
                      <SelectTrigger id={`start-month-${edu.id}`}>
                        <SelectValue placeholder={placeholdersTrans["selectMonth"]} />
                      </SelectTrigger>
                      <SelectContent>
                        {months.map((m: string) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor={`start-year-${edu.id}`}>{labelsTrans["startYear"]}</Label>
                    <Input
                      id={`start-year-${edu.id}`}
                      placeholder={placeholdersTrans["selectYear"]}
                      value={edu.startYear}
                      onChange={(e) => updateEducation(edu.id, "startYear", e.target.value)}
                    />
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor={`end-month-${edu.id}`}>{labelsTrans["endMonth"]}</Label>
                    <Select value={edu.endMonth} onValueChange={(value) => updateEducation(edu.id, "endMonth", value)}>
                      <SelectTrigger id={`end-month-${edu.id}`}>
                        <SelectValue placeholder={placeholdersTrans["selectMonth"]} />
                      </SelectTrigger>
                      <SelectContent>
                        {months.map((m: string) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor={`end-year-${edu.id}`}>{AutoTranslate(`End Year`)}</Label>
                    <Input
                      id={`end-year-${edu.id}`}
                      placeholder={placeholdersTrans["selectYear"]}
                      value={edu.endYear}
                      onChange={(e) => updateEducation(edu.id, "endYear", e.target.value)}
                    />
                  </div>
                </div>
              </>
            )}

            <div className="space-y-2">
              <Label htmlFor={`awards-${edu.id}`}>{labelsTrans["awards"]}</Label>
              <Input
                id={`awards-${edu.id}`}
                value={edu.awards || ""}
                onChange={(e) => updateEducation(edu.id, "awards", e.target.value)}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor={`grades-${edu.id}`}>{labelsTrans["grades"]}</Label>
              <Input
                id={`grades-${edu.id}`}
                value={edu.grades || ""}
                onChange={(e) => updateEducation(edu.id, "grades", e.target.value)}
                placeholder={placeholdersTrans["grade"]}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor={`project-${edu.id}`}>{labelsTrans["project"]}</Label>
              <Textarea
                id={`project-${edu.id}`}
                value={edu.project || ""}
                onChange={(e) => updateEducation(edu.id, "project", e.target.value)}
              />
            </div>
          </div>
        ))}
        <Button type="button" variant="outline" onClick={addEducation} className="w-full">
          <Plus className="h-4 w-4 mr-2" />
          {buttonsTrans["addEducation"]}
        </Button>
      </div>

      <div className="space-y-4">
        <h3 className="text-xl font-semibold">{labelsTrans["skills"]}</h3>

        {isRecommendationLoading && (
          <div className="overflow-hidden rounded-xl border border-primary/10 bg-gradient-to-r from-muted/60 to-muted/30 p-4 shadow-sm">
            <div className="flex items-center gap-3">
              <div className="flex h-10 w-10 items-center justify-center rounded-full bg-background/90 shadow-sm">
                <Loader2 className="h-5 w-5 animate-spin text-primary" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-primary" />
                  <p className="text-sm font-semibold text-foreground">Finding skill recommendations</p>
                </div>
                <p className="text-xs text-muted-foreground">Matching suggestions to the selected role...</p>
              </div>
            </div>

            <div className="mt-4 flex flex-wrap gap-2">
              <div className="h-8 w-24 animate-pulse rounded-full bg-background/80" />
              <div className="h-8 w-32 animate-pulse rounded-full bg-background/80" />
              <div className="h-8 w-28 animate-pulse rounded-full bg-background/80" />
              <div className="h-8 w-20 animate-pulse rounded-full bg-background/80" />
            </div>
          </div>
        )}

        {!isRecommendationLoading && recommendedSoftSkills.length === 0 && recommendedTechnicalSkills.length === 0 && jobTitle.trim().length >= 3 && (
          <p className="text-sm text-muted-foreground">No skill recommendations found.</p>
        )}

        {!isRecommendationLoading && (recommendedSoftSkills.length > 0) && (
          <div className="space-y-4 rounded-xl border border-primary/10 bg-gradient-to-r from-muted/60 to-muted/30 p-4 shadow-sm">
            {recommendedSoftSkills.length > 0 && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-2">Recommended Soft Skills</p>
                <div className="flex flex-wrap gap-2">
                  {recommendedSoftSkills.map((skill) => {
                    const disabled = hasSoftSkill(skill.name);
                    return (
                      <Button
                        key={`rec-soft-${skill.name}`}
                        type="button"
                        size="sm"
                        variant={disabled ? "secondary" : "outline"}
                        disabled={disabled}
                        className="h-8 px-3 text-xs font-medium gap-1 rounded-full"
                        onClick={() => addSkill("soft", skill.name)}
                        title={disabled ? "Already added" : "Add skill"}
                      >
                        <Plus className="h-3.5 w-3.5" />
                        {skill.name}
                      </Button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}

        <div className="space-y-2">
          <Label htmlFor="soft-skills-input">{labelsTrans["softSkills"]}</Label>
          {suggestedSoftSkills.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2">
              <span className="text-sm text-muted-foreground">{labelsTrans["suggestions"]}</span>
              {suggestedSoftSkills.map((skill) => (
                <Badge
                  key={skill}
                  variant="outline"
                  className="cursor-pointer hover:bg-primary hover:text-primary-foreground"
                  role="button"
                  tabIndex={0}
                  onClick={() => addSkill("soft", skill)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); addSkill("soft", skill); } }}
                  aria-label={`Add ${skill}`}
                >
                  + {skill}
                </Badge>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <Input
              id="soft-skills-input"
              value={softSkillInput}
              onChange={(e) => setSoftSkillInput(e.target.value)}
              placeholder={placeholdersTrans["addSoftSkill"]}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addSkill("soft", softSkillInput);
                }
              }}
            />
            <Button type="button" onClick={() => addSkill("soft", softSkillInput)}>
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            {formData.softSkills.map((skill) => (
              <Badge key={skill} variant="secondary" className="cursor-pointer" role="button" tabIndex={0} onClick={() => removeSkill("soft", skill)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); removeSkill("soft", skill); } }} aria-label={`Remove ${skill}`}>
                {skill} <X className="h-3 w-3 ml-1" />
              </Badge>
            ))}
          </div>
        </div>

        {!isRecommendationLoading && (recommendedTechnicalSkills.length > 0) && (
          <div className="space-y-4 rounded-xl border border-primary/10 bg-gradient-to-r from-muted/60 to-muted/30 p-4 shadow-sm">
            {recommendedTechnicalSkills.length > 0 && (
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-2">Recommended Technical Skills</p>
                <div className="flex flex-wrap gap-2">
                  {recommendedTechnicalSkills.map((skill) => {
                    const disabled = hasTechnicalSkill(skill.name);
                    return (
                      <Button
                        key={`rec-technical-${skill.name}`}
                        type="button"
                        size="sm"
                        variant={disabled ? "secondary" : "outline"}
                        disabled={disabled}
                        className="h-8 px-3 text-xs font-medium gap-1 rounded-full"
                        onClick={() => addSkill("technical", skill.name)}
                        title={disabled ? "Already added" : "Add skill"}
                      >
                        <Plus className="h-3.5 w-3.5" />
                        {skill.name}
                      </Button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>
        )}
        <div className="space-y-2">
          <Label htmlFor="technical-skills-input">{labelsTrans["technicalSkills"]}</Label>
          {suggestedTechnicalSkills.length > 0 && (
            <div className="flex flex-wrap gap-2 mb-2">
              <span className="text-sm text-muted-foreground">{labelsTrans["suggestions"]}</span>
              {suggestedTechnicalSkills.map((skill) => (
                <Badge
                  key={skill}
                  variant="outline"
                  className="cursor-pointer hover:bg-primary hover:text-primary-foreground"
                  role="button"
                  tabIndex={0}
                  onClick={() => addSkill("technical", skill)}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); addSkill("technical", skill); } }}
                  aria-label={`Add ${skill}`}
                >
                  + {skill}
                </Badge>
              ))}
            </div>
          )}
          <div className="flex gap-2">
            <Input
              id="technical-skills-input"
              value={technicalSkillInput}
              onChange={(e) => setTechnicalSkillInput(e.target.value)}
              placeholder={placeholdersTrans["addTechnicalSkill"]}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addSkill("technical", technicalSkillInput);
                }
              }}
            />
            <Button type="button" onClick={() => addSkill("technical", technicalSkillInput)}>
              <Plus className="h-4 w-4" />
            </Button>
          </div>
          <div className="flex flex-wrap gap-2 mt-2">
            {formData.technicalSkills.map((skill) => (
              <Badge key={skill} variant="secondary" className="cursor-pointer" role="button" tabIndex={0} onClick={() => removeSkill("technical", skill)} onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); removeSkill("technical", skill); } }} aria-label={`Remove ${skill}`}>
                {skill} <X className="h-3 w-3 ml-1" />
              </Badge>
            ))}
          </div>
        </div>
      </div>

      <div className="space-y-4">
        <h3 className="text-xl font-semibold">{labelsTrans["languages"]}</h3>
        {formData.languages.map((lang) => (
          <div key={lang.id} className="grid grid-cols-2 gap-4 p-4 border rounded-lg relative">
            {formData.languages.length > 1 && (
              <Button
                type="button"
                variant="destructive"
                size="icon"
                className="absolute top-2 right-2"
                aria-label="Remove language"
                onClick={() => removeLanguage(lang.id)}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            )}
            <div className="space-y-2">
              <Label htmlFor={`language-name-${lang.id}`}>{labelsTrans["language"]}</Label>
              <Input
                id={`language-name-${lang.id}`}
                value={lang.name}
                onChange={(e) => updateLanguage(lang.id, "name", e.target.value)}
                placeholder="Spanish"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor={`language-proficiency-${lang.id}`}>{labelsTrans["proficiency"]}</Label>
              <Select value={lang.proficiency} onValueChange={(value) => updateLanguage(lang.id, "proficiency", value)}>
                <SelectTrigger id={`language-proficiency-${lang.id}`}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {proficiencyLevels.map((level: string) => (
                    <SelectItem key={level} value={level}>{level}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        ))}
        <Button type="button" variant="outline" onClick={addLanguage} className="w-full">
          <Plus className="h-4 w-4 mr-2" />
          {buttonsTrans["addLanguage"]}
        </Button>
      </div>

      <div className="space-y-4">
        <h3 className="text-xl font-semibold">{labelsTrans["certifications"]}</h3>
        {formData.certifications.map((cert) => (
          <div key={cert.id} className="space-y-4 p-4 border rounded-lg relative">
            <Button
              type="button"
              variant="destructive"
              size="icon"
              className="absolute top-2 right-2"
              aria-label="Remove certification"
              onClick={() => removeCertification(cert.id)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
            <div className="space-y-2">
              <Label htmlFor={`certification-name-${cert.id}`}>{labelsTrans["certificationName"]}</Label>
              <Input
                id={`certification-name-${cert.id}`}
                value={cert.name}
                onChange={(e) => updateCertification(cert.id, "name", e.target.value)}
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor={`certification-month-${cert.id}`}>{labelsTrans["month"]}</Label>
                <Select value={cert.month} onValueChange={(value) => updateCertification(cert.id, "month", value)}>
                  <SelectTrigger id={`certification-month-${cert.id}`}>
                    <SelectValue placeholder={buttonsTrans["selectMonth"]} />
                  </SelectTrigger>
                  <SelectContent>
                    {months.map((m: string) => <SelectItem key={m} value={m}>{m}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label htmlFor={`certification-year-${cert.id}`}>{labelsTrans["year"]}</Label>
                <Input
                  id={`certification-year-${cert.id}`}
                  value={cert.year}
                  onChange={(e) => updateCertification(cert.id, "year", e.target.value)}
                />
              </div>
            </div>
          </div>
        ))}
        <Button type="button" variant="outline" onClick={addCertification} className="w-full">
          <Plus className="h-4 w-4 mr-2" />
          {buttonsTrans["addCertification"]}
        </Button>
      </div>

      <div className="space-y-4">
        <h3 className="text-xl font-semibold">{labelsTrans["references"]}</h3>
        <p className="text-sm text-muted-foreground">{labelsTrans["leaveBlank"]}</p>
        {formData.referees.map((ref) => (
          <div key={ref.id} className="space-y-4 p-4 border rounded-lg relative">
            <Button
              type="button"
              variant="destructive"
              size="icon"
              className="absolute top-2 right-2"
              aria-label="Remove reference"
              onClick={() => removeReferee(ref.id)}
            >
              <Trash2 className="h-4 w-4" />
            </Button>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor={`referee-name-${ref.id}`}>{labelsTrans["name"]}</Label>
                <Input
                  id={`referee-name-${ref.id}`}
                  value={ref.name}
                  onChange={(e) => updateReferee(ref.id, "name", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor={`referee-position-${ref.id}`}>{labelsTrans["position"]}</Label>
                <Input
                  id={`referee-position-${ref.id}`}
                  value={ref.position}
                  onChange={(e) => updateReferee(ref.id, "position", e.target.value)}
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor={`referee-company-${ref.id}`}>{labelsTrans["company"]}</Label>
                <Input
                  id={`referee-company-${ref.id}`}
                  value={ref.company}
                  onChange={(e) => updateReferee(ref.id, "company", e.target.value)}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor={`referee-email-${ref.id}`}>{labelsTrans["email"]}</Label>
                <Input
                  type="email"
                  id={`referee-email-${ref.id}`}
                  value={ref.email}
                  onChange={(e) => updateReferee(ref.id, "email", e.target.value)}
                />
              </div>
            </div>
          </div>
        ))}
        <Button type="button" variant="outline" onClick={addReferee} className="w-full">
          <Plus className="h-4 w-4 mr-2" />
          {buttonsTrans["addReference"]}
        </Button>
      </div>

      <Button type="submit" className="w-full sticky bottom-0">{AutoTranslate(`Send`)}</Button>
    </form>
  );
 }